/**
 * Gider pusulası şartları — pusulada hangi ürün, ne miktarda yazılacak.
 *
 * KURALLAR (kullanıcı, 02.10.2026):
 *  1. İşlemde döviz varsa pusulada DÖVİZ YAZMAZ; altına çevrilir.
 *  2. İşlem tutarı ile pusula tutarı farklıysa (kısmi havale) ürün ve miktar
 *     PUSULA TUTARINA göre yeniden hesaplanır.
 *  3. Döviz değilse ve tutarlar aynıysa işlemdeki ürün ve miktar aynen yazılır.
 *  4. Fiyat, Güncel Fiyat Ekranı'ndaki ALIŞ fiyatlarından okunur (gider pusulası
 *     bir alım belgesidir; satış fiyatı kullanılmaz).
 *  5. Çevrilecekse tutara göre kademe:
 *        ≤ 10.000 TL → 8 Ayar Hurda
 *        ≤ 20.000 TL → 14 Ayar Hurda
 *        > 20.000 TL → 22 Ayar Bilezik Hurda
 *
 * Kademe fiyatları fiyat ekranındaki kendi formüllerinden gelir
 * (8 Ayar / 14 Ayar / 22 Ayar Bilezik — hepsi ALTINTRY alış üzerinden).
 * Fiyat okunamazsa (ör. "acil" modda tablo üretilmez) kalem üretilmez ve
 * `fiyatYok` işaretlenir; yanlış fiyattan pusula kesmektense boş bırakmak
 * güvenli.
 */

import { lookupLiveProductPrice, resolveLiveProductBuyPrices } from './invoiceLivePriceTable';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Tutar kademeleri. Fiyat için `formul`, pusulada yazılacak ad için `urun`. */
export const GIDER_PUSULASI_KADEMELERI = [
  { enFazla: 10000, urun: '8 Ayar Hurda', formul: '8 Ayar' },
  { enFazla: 20000, urun: '14 Ayar Hurda', formul: '14 Ayar' },
  { enFazla: Infinity, urun: '22 Ayar Bilezik Hurda', formul: '22 Ayar Bilezik' }
];

export const kademeSec = (tutar) => {
  const t = round2(tutar);
  return GIDER_PUSULASI_KADEMELERI.find((k) => t <= k.enFazla) || GIDER_PUSULASI_KADEMELERI[GIDER_PUSULASI_KADEMELERI.length - 1];
};

const DOVIZ_DESEN = /d[öo]viz|dolar|euro|avro|sterlin|frang|dinar|riyal|dirhem|\b(usd|eur|gbp|chf|sar|aed|kwd|jod|qar|cad|aud)\b/i;

/** İşlem döviz alımı mı? */
export const dovizMi = (aday) => DOVIZ_DESEN.test(`${aday?.kategori || ''} ${aday?.urun || ''}`);

/** Pusula tutarı işlem tutarından farklı mı (kısmi havale)? */
export const tutarFarkliMi = (aday) =>
  Math.abs(round2(aday?.islemTutari) - round2(aday?.pusulaTutari)) > 0.01;

/**
 * Pusulaya yazılacak kalemi üretir.
 *
 * @param {object} aday  giderPusulasiAdaylari çıktısı
 * @param {object} settings  fatura ayarları (alış fiyat tablosu için)
 * @returns {{ isinMahiyeti, cinsi, miktar, birim, fiyat, tutar, cevrildi, kademe, fiyatYok, sebep }}
 */
export const pusulaKalemiUret = (aday, settings = null) => {
  const tutar = round2(aday?.pusulaTutari);
  if (!(tutar > 0)) return null;

  const doviz = dovizMi(aday);
  const farkli = tutarFarkliMi(aday);

  // 3. kural: döviz değil ve tutar birebir → işlemdeki ürün aynen yazılır.
  if (!doviz && !farkli) {
    const miktar = round2(aday.gram > 0 ? aday.gram : aday.adet || 1);
    return {
      isinMahiyeti: aday.kategori || '',
      cinsi: aday.urun || '',
      miktar,
      birim: aday.gram > 0 ? 'Gram' : 'Adet',
      fiyat: miktar > 0 ? round2(tutar / miktar) : tutar,
      tutar,
      cevrildi: false,
      kademe: null,
      fiyatYok: false,
      sebep: ''
    };
  }

  // 1., 2. ve 5. kural: altına çevrilir, kademe tutara göre seçilir.
  const kademe = kademeSec(tutar);
  const buyTable = resolveLiveProductBuyPrices(settings);
  const birimFiyat = round2(lookupLiveProductPrice(kademe.formul, buyTable));
  const sebep = doviz ? 'döviz alımı altına çevrildi' : 'kısmi ödeme — pusula tutarına göre hesaplandı';

  if (!(birimFiyat > 0)) {
    return {
      isinMahiyeti: 'Hurda Altın',
      cinsi: kademe.urun,
      miktar: '',
      birim: 'Gram',
      fiyat: '',
      tutar,
      cevrildi: true,
      kademe: kademe.urun,
      fiyatYok: true,
      sebep: `${sebep} · ${kademe.formul} alış fiyatı okunamadı, miktarı elle girin`
    };
  }

  return {
    isinMahiyeti: 'Hurda Altın',
    cinsi: kademe.urun,
    miktar: round2(tutar / birimFiyat),
    birim: 'Gram',
    fiyat: birimFiyat,
    tutar,
    cevrildi: true,
    kademe: kademe.urun,
    fiyatYok: false,
    sebep
  };
};
